import { useEffect, useMemo, useState } from "react";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { GET_PROMOTIONS } from "src/constants/queries";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { BtnLoader } from "src/Loader";
import { formatDate } from "src/utils/date";
import { failureToast } from "src/utils/toasts";

type Promotion = {
  correlationId: string;
  name: string;
  status: string;
  startsAt?: number | null;
  expiresAt?: number | null;
  maxRedemptionsTotal?: number | null;
  maxRedemptionsPerCustomer?: number | null;
  firstTimeCustomerOnly?: boolean | null;
};

const ExistingCustomerPromotions = () => {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [loading, setLoading] = useState(false);
  const [promotions, setPromotions] = useState<Promotion[]>([]);

  useEffect(() => {
    const loadPromotions = async () => {
      if (!hasRestaurant?.correlationId) return;
      setLoading(true);
      try {
        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_PROMOTIONS,
          variables: { storeCorrelationId: hasRestaurant.correlationId },
        });
        setPromotions(data?.data?.promotionsByStore || []);
      } catch {
        failureToast("Unable to load promotions insights.");
      } finally {
        setLoading(false);
      }
    };
    loadPromotions();
  }, [axBe, hasRestaurant?.correlationId]);

  const stats = useMemo(() => {
    const active = promotions.filter((promotion) => promotion.status === "ACTIVE").length;
    const draft = promotions.filter((promotion) => promotion.status === "DRAFT").length;
    const paused = promotions.filter((promotion) => promotion.status === "PAUSED").length;
    const archived = promotions.filter((promotion) => promotion.status === "ARCHIVED").length;
    const expiringSoon = promotions.filter((promotion) => {
      if (!promotion.expiresAt) return false;
      const daysToExpiry = promotion.expiresAt - Date.now();
      return daysToExpiry > 0 && daysToExpiry <= 1000 * 60 * 60 * 24 * 7;
    }).length;
    return { active, draft, paused, archived, expiringSoon };
  }, [promotions]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <div className="rounded-xl bg-white p-4">
          <p className="text-xs text-[#9B9B9B]">Active</p>
          <p className="mt-1 text-2xl font-semibold">{stats.active}</p>
        </div>
        <div className="rounded-xl bg-white p-4">
          <p className="text-xs text-[#9B9B9B]">Draft</p>
          <p className="mt-1 text-2xl font-semibold">{stats.draft}</p>
        </div>
        <div className="rounded-xl bg-white p-4">
          <p className="text-xs text-[#9B9B9B]">Paused</p>
          <p className="mt-1 text-2xl font-semibold">{stats.paused}</p>
        </div>
        <div className="rounded-xl bg-white p-4">
          <p className="text-xs text-[#9B9B9B]">Archived</p>
          <p className="mt-1 text-2xl font-semibold">{stats.archived}</p>
        </div>
        <div className="rounded-xl bg-white p-4 col-span-2 sm:col-span-1">
          <p className="text-xs text-[#9B9B9B]">Expiring in 7 days</p>
          <p className="mt-1 text-2xl font-semibold">{stats.expiringSoon}</p>
        </div>
      </div>

      <div className="rounded-xl bg-white p-4 sm:p-5">
        <h3 className="text-base font-semibold text-[#252525]">Promotion insights</h3>
        {loading ? (
          <div className="py-6 text-center">
            <BtnLoader />
          </div>
        ) : promotions.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#9B9B9B]">No promotions yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[720px] w-full text-left text-sm">
              <thead className="border-b border-borderGray text-xs text-[#9B9B9B]">
                <tr>
                  <th className="px-3 py-3">Promotion</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Start</th>
                  <th className="px-3 py-3">Expiry</th>
                  <th className="px-3 py-3">Max total</th>
                  <th className="px-3 py-3">Max per customer</th>
                </tr>
              </thead>
              <tbody>
                {promotions.map((promotion) => (
                  <tr key={promotion.correlationId} className="border-b border-[#EBF5FF]">
                    <td className="px-3 py-3 font-medium">{promotion.name}</td>
                    <td className="px-3 py-3">{promotion.status}</td>
                    <td className="px-3 py-3">
                      {promotion.startsAt ? formatDate(promotion.startsAt) : "-"}
                    </td>
                    <td className="px-3 py-3">
                      {promotion.expiresAt ? formatDate(promotion.expiresAt) : "-"}
                    </td>
                    <td className="px-3 py-3">{promotion.maxRedemptionsTotal ?? "-"}</td>
                    <td className="px-3 py-3">{promotion.maxRedemptionsPerCustomer ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExistingCustomerPromotions;
